import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  handleTextInputChange,
  handleSearchInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../../utils/formValidation';
import { truncateText, formatDate, capitalizeWords, capitalizeSentences } from '../../../utils/stringUtils';
import ticketService from '../../../api/crm/support/ticketService';
import customerService from '../../../api/crm/customers/customerService';
import orderService from '../../../api/crm/orders/orderService';
import userService from '../../../api/users/userService';
import ExportModal from '../../../components/common/ExportModal';
import { generateExportFilename, downloadFromBackend, exportToCSV } from '../../../utils/exportUtils';
import { API_ENDPOINTS } from '../../../api/config';
import {
  Plus,
  Search,
  Filter,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  Tag,
  MessageSquare,
  FileText,
  Download,
  Edit,
  Trash2,
  Eye,
  TrendingUp,
  Users,
  Settings,
  Bell,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  MoreVertical,
  Phone,
  Mail,
  MessageCircle,
  ExternalLink,
  Archive,
  RefreshCw,
  Image,
  Upload,
  Ticket,
  Paperclip,
  ArrowLeft,
  X,
} from "lucide-react";
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import PermissionGate from '../../../components/common/PermissionGate';
import SearchableDropdown from '../../../components/common/SearchableDropdown/SearchableDropdown';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import BulkTicketUpload from './BulkTicketUpload';

const TicketsFeedback = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Export State
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [ticketIdForDetail, setTicketIdForDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [errors, setErrors] = useState({});
  const searchInputRef = useRef(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filter state
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  // Sorting state
  const [sortColumn, setSortColumn] = useState('created');
  const [sortDirection, setSortDirection] = useState('desc');

  // Customer search and order fetch states
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [editingAssignee, setEditingAssignee] = useState(null);

  // React Query: Fetch KPIs with status filter
  const {
    data: kpisData,
    isLoading: loadingKPIs
  } = useQuery({
    queryKey: ['ticketKPIs', filterStatus],
    queryFn: async () => {
      const result = await ticketService.getTicketKPIs(filterStatus !== 'all' ? filterStatus : undefined);
      if (result.success) {
        return {
          totalTickets: result.totalTickets || 0,
          openTickets: result.openTickets || 0,
          inProgressTickets: result.inProgressTickets || 0,
          pendingTickets: result.pendingTickets || 0,
          resolvedTickets: result.resolvedTickets || 0,
          closedTickets: result.closedTickets || 0
        };
      }
      throw new Error(result.error || 'Failed to load KPIs');
    }
  });

  const kpis = kpisData || {
    totalTickets: 0,
    openTickets: 0,
    inProgressTickets: 0,
    pendingTickets: 0,
    resolvedTickets: 0,
    closedTickets: 0
  };

  // React Query: Fetch tickets with pagination and search
  const {
    data: ticketsData,
    isLoading: loadingTickets,
    isError: ticketsError,
    error: ticketsErrorData
  } = useQuery({
    queryKey: ['tickets', debouncedSearchTerm, filterStatus, filterPriority, currentPage, itemsPerPage, dateRange],
    queryFn: async () => {
      const filters = {
        search: debouncedSearchTerm.trim() || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        priority: filterPriority !== 'all' ? filterPriority : undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        page: currentPage,
        limit: itemsPerPage
      };

      const result = await ticketService.getTickets(filters);

      if (result.success) {
        // Transform API response to match component format
        const transformedTickets = (result.data || []).map(ticket => ({
          id: ticket._id,
          ticketId: ticket.ticketId,
          title: ticket.title,
          description: ticket.description,
          customer: ticket.customerName,
          business: ticket.businessName || '',
          priority: ticket.priority,
          status: ticket.status,
          category: ticket.category || '',
          subCategory: ticket.subCategory || '',
          source: ticket.source,
          assignee: ticket.assignee ? `${ticket.assignee.firstName} ${ticket.assignee.lastName}` : 'Unassigned',
          assigneeId: ticket.assignee?._id || null,
          created: ticket.createdAt,
          updated: ticket.updatedAt,
          tags: ticket.tags || [],
          attachments: ticket.attachments || [],
          linkedOrder: ticket.orderId ? (typeof ticket.orderId === 'object' ? (ticket.orderId.orderNumber || ticket.orderId.orderId) : ticket.orderId) : '',
          linkedOrderId: ticket.orderId ? (typeof ticket.orderId === 'object' ? (ticket.orderId._id || ticket.orderId.id) : ticket.orderId) : '',
          resolution: ticket.resolution || null,
          timeline: ticket.timeline || [],
          _id: ticket._id,
          customerId: (ticket.customerId && ticket.customerId._id) || ticket.customerId || null
        }));

        return {
          tickets: transformedTickets,
          pagination: {
            currentPage: result.page || 1,
            totalPages: result.totalPages || 1,
            totalItems: result.totalItems || 0,
            limit: result.limit || itemsPerPage,
            hasNextPage: result.hasNextPage || false,
            hasPrevPage: result.hasPrevPage || false
          }
        };
      } else {
        throw new Error(result.error || 'Failed to load tickets');
      }
    },
    keepPreviousData: true
  });

  const tickets = ticketsData?.tickets || [];
  const pagination = ticketsData?.pagination || null;

  // React Query: Fetch customer orders
  const customerId = selectedCustomer?._id || selectedCustomer?.id || null;
  const {
    data: customerOrdersData,
    isLoading: loadingOrders
  } = useQuery({
    queryKey: ['customerOrders', customerId],
    queryFn: async () => {
      if (!customerId) return [];
      const result = await orderService.getOrdersByCustomer(customerId);
      if (result.success) {
        return result.data || [];
      }
      throw new Error(result.error || 'Failed to load orders');
    },
    enabled: !!customerId
  });

  const customerOrders = customerOrdersData || [];

  // Autofocus search bar on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
      setSelectedRows(new Set()); // Reset selection on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterPriority]);

  // Fetch functions for SearchableDropdown

  // Fetch top 6 customers (default)
  const fetchTopCustomers = async () => {
    try {
      const result = await customerService.getCustomers({ limit: 6 });
      if (result.success) {
        return result.customers || [];
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  // Fetch customers by search
  const fetchCustomerSearch = async (searchTerm) => {
    try {
      const result = await customerService.getCustomers({ search: searchTerm, limit: 20 });
      if (result.success) {
        return result.customers || [];
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  // Fetch CRM team users (same as lead assignment dropdown)
  const fetchCRMUsers = async (searchTerm = '') => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      params.append('limit', '50');
      // Only fetch support team members (Support Manager) for ticket assignment
      params.append('roleFilter', 'support');

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        const crmUsers = result.data?.users || [];

        // Transform users to match the format expected by SearchableDropdown
        return crmUsers.map(user => ({
          _id: user.user || user._id || user.id,
          id: user.user || user._id || user.id,
          firstName: user.name?.split(' ')[0] || user.name || 'Unknown',
          lastName: user.name?.split(' ').slice(1).join(' ') || '',
          name: user.name || 'Unknown',
          email: user.email || '',
          role: user.role || '',
          department: user.department || '',
          designation: user.designation || ''
        })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  // Fetch top CRM users (default) - same as lead assignment
  const fetchTopSupportUsers = async () => {
    return await fetchCRMUsers('');
  };

  // Fetch CRM users by search - same as lead assignment
  const fetchSupportUserSearch = async (searchTerm) => {
    return await fetchCRMUsers(searchTerm);
  };

  // Render customer item
  const renderCustomerItem = (customer, isSelected = false) => {
    if (isSelected) {
      return customer.companyName || customer.contactPerson || customer.contactPersonName || '';
    }
    return (
      <div>
        <div className="font-medium text-gray-900">{customer.companyName || customer.name || 'Unnamed'}</div>
        <div className="text-xs text-gray-500">{customer.contactPerson || customer.contactPersonName || customer.email || ''}</div>
      </div>
    );
  };

  // Render assignee item
  const renderAssigneeItem = (user, isSelected = false) => {
    if (isSelected) {
      return `${user.firstName} ${user.lastName}`;
    }
    return (
      <div>
        <div className="font-medium text-gray-900">{user.firstName} {user.lastName}</div>
        <div className="text-xs text-gray-500">{user.designation || user.department || user.role || 'Support'}</div>
      </div>
    );
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    if (!customer) {
      setSelectedCustomer(null);

      const resetData = {
        customer: '',
        business: '',
        linkedOrder: ''
      };

      if (editingTicket) {
        setEditingTicket(prev => ({ ...prev, ...resetData }));
      } else {
        setNewTicket(prev => ({ ...prev, ...resetData }));
      }
      return;
    }

    setSelectedCustomer(customer);
    const customerName = customer.contactPerson || customer.contactPersonName || customer.companyName;

    const updateData = {
      customer: customerName,
      business: customer.companyName || '',
      linkedOrder: '' // Reset linked order when customer changes
    };

    if (editingTicket) {
      setEditingTicket(prev => ({ ...prev, ...updateData }));
    } else {
      setNewTicket(prev => ({ ...prev, ...updateData }));
    }

    if (errors.customer) {
      setErrors(prev => ({ ...prev, customer: '' }));
    }
  };

  // Handle assignee selection
  const handleAssigneeSelect = (assignee) => {
    if (!assignee) {
      setSelectedAssignee(null);
      setNewTicket({
        ...newTicket,
        assignee: ''
      });
      return;
    }

    setSelectedAssignee(assignee);
    setNewTicket({
      ...newTicket,
      assignee: assignee._id || assignee.id || ''
    });
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedRows(new Set());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
    setSelectedRows(new Set());
  };

  // Handle filter change
  const handleFilterChange = (newFilterStatus) => {
    setFilterStatus(newFilterStatus);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle priority filter change
  const handlePriorityFilterChange = (newFilterPriority) => {
    setFilterPriority(newFilterPriority);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // **NEW: Create Ticket Form States**
  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    customer: "",
    business: "",
    priority: "medium",
    category: "general",
    subCategory: "",
    source: "manual",
    assignee: "Support Team",
    linkedOrder: "",
    tags: [],
    attachments: [],
  });

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted tickets results
  const getSortedTickets = (data) => {
    if (!sortColumn || !data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'ticketId') {
        // Handle numeric part of ticket ID if possible, otherwise string sort
        aValue = (a.ticketId || a.id || '').toLowerCase();
        bValue = (b.ticketId || b.id || '').toLowerCase();
      } else if (sortColumn === 'title') {
        aValue = (a.title || '').toLowerCase();
        bValue = (b.title || '').toLowerCase();
      } else if (sortColumn === 'customer') {
        aValue = (a.customer || '').toLowerCase();
        bValue = (b.customer || '').toLowerCase();
      } else if (sortColumn === 'priority') {
        // Map priorities to numeric values for correct sorting
        const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
        aValue = priorityOrder[a.priority?.toLowerCase()] || 0;
        bValue = priorityOrder[b.priority?.toLowerCase()] || 0;
      } else if (sortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (sortColumn === 'created') {
        aValue = new Date(a.created || 0).getTime();
        bValue = new Date(b.created || 0).getTime();
      } else if (sortColumn === 'assignee') {
        aValue = (a.assignee || '').toLowerCase();
        bValue = (b.assignee || '').toLowerCase();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      const order = sortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Old default tickets removed - now loaded from API
  const [oldTickets] = useState([]);




  // **NEW: Form Options**
  const ticketCategories = [
    { value: "general", label: "General Inquiry" },
    { value: "delivery", label: "Delivery Issue" },
    { value: "quality", label: "Product Quality" },
    { value: "billing", label: "Billing & Payment" },
    { value: "technical", label: "Technical Support" },
    { value: "complaint", label: "Complaint" },
  ];

  const ticketSources = [
    { value: "manual", label: "Manual Entry" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "email", label: "Email" },
    { value: "call", label: "Phone Call" },
    { value: "website", label: "Website" },
    { value: "mobile_app", label: "Mobile App" },
  ];


  // React Query: Create ticket mutation
  const createTicketMutation = useMutation({
    mutationFn: async (ticketData) => {
      const result = await ticketService.createTicket(ticketData);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || 'Failed to create ticket');
    },
    onSuccess: (result) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticketKPIs'] });

      // Reset form
      setNewTicket({
        title: "",
        description: "",
        customer: "",
        business: "",
        priority: "medium",
        category: "general",
        subCategory: "",
        source: "manual",
        assignee: "",
        linkedOrder: "",
        tags: [],
        attachments: [],
      });
      setSelectedCustomer(null);
      setSelectedAssignee(null);
      setErrors({});
      setShowCreateModal(false);

      if (window._customAlert && window._customAlert.showSuccess) {
        window._customAlert.showSuccess(`Ticket ${result.ticket.ticketId} created successfully!`);
      } else {
        alert(`Ticket ${result.ticket.ticketId} created successfully!`);
      }
    },
    onError: (error) => {
      if (window._customAlert && window._customAlert.showError) {
        window._customAlert.showError(error.message || 'Failed to create ticket');
      } else {
        alert(error.message || 'Failed to create ticket');
      }
    }
  });

  // Handle blur for Create Ticket
  const handleCreateTicketBlur = (field) => {
    const newErrors = { ...errors };
    if (field === 'title') {
      if (!newTicket.title || !newTicket.title.trim()) {
        newErrors.title = 'Title is required';
      }
    } else if (field === 'description') {
      if (!newTicket.description || !newTicket.description.trim()) {
        newErrors.description = 'Description is required';
      }
    } else if (field === 'customer') {
      if (!selectedCustomer) {
        newErrors.customer = 'Customer is required';
      }
    }
    setErrors(newErrors);
  };

  // Handle blur for Update Ticket
  const handleUpdateTicketBlur = (field) => {
    const newErrors = { ...errors };
    if (field === 'title') {
      if (!editingTicket.title || !editingTicket.title.trim()) {
        newErrors.title = 'Title is required';
      }
    } else if (field === 'description') {
      if (!editingTicket.description || !editingTicket.description.trim()) {
        newErrors.description = 'Description is required';
      }
    }
    setErrors(newErrors);
  };

  // **NEW: Create Ticket Handler**
  const handleCreateTicket = async () => {
    // Reset errors
    setErrors({});

    // Validate required fields
    const newErrors = {};

    if (!newTicket.title || !newTicket.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (!VALID_TEXT_REGEX.test(newTicket.title)) {
      newErrors.title = 'Title contains invalid characters';
    }

    if (!newTicket.description || !newTicket.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (!VALID_TEXT_REGEX.test(newTicket.description)) {
      newErrors.description = 'Description contains invalid characters';
    }

    if (!selectedCustomer) {
      newErrors.customer = 'Customer is required';
    }

    // If there are errors, set them and return
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error field
      setTimeout(() => {
        const firstErrorField = Object.keys(newErrors)[0];
        let errorElement = null;

        if (firstErrorField === 'customer') {
          // For customer dropdown, find the input inside SearchableDropdown
          errorElement = document.querySelector('label:has-text("Customer")')?.nextElementSibling?.querySelector('input') ||
            document.querySelector('[placeholder*="customer" i]');
        } else {
          errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        }

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }, 100);
      return;
    }

    const customerId = selectedCustomer._id || selectedCustomer.id;
    // Safely derive orderId - treat "None" / "No orders" as no selection
    const linkedOrderValue = (newTicket.linkedOrder || '').toString().trim();
    const orderId =
      linkedOrderValue &&
        linkedOrderValue !== 'none' &&
        linkedOrderValue !== 'no_orders'
        ? linkedOrderValue
        : null;

    const ticketData = {
      title: newTicket.title.trim(),
      description: newTicket.description.trim(),
      customerId: customerId,
      customerName: newTicket.customer.trim(),
      businessName: newTicket.business.trim() || '',
      priority: newTicket.priority,
      category: newTicket.category,
      subCategory: newTicket.subCategory || '',
      source: newTicket.source,
      assignee: newTicket.assignee && newTicket.assignee !== 'none' ? newTicket.assignee : null,
      orderId: orderId,
      tags: newTicket.tags || [],
      attachments: newTicket.attachments || []
    };

    createTicketMutation.mutate(ticketData);
  };

  // **NEW: Selection and Export Handlers**

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = tickets.map(t => t.id || t._id || t.ticketId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const mapTicketsForExport = (ticketsToExport) => {
    return ticketsToExport.map((ticket) => ({
      "Ticket ID": ticket.ticketId || ticket.id || '',
      "Title": ticket.title || '',
      "Customer": ticket.customer || '',
      "Contact Person": ticket.contactPerson || '',
      "Priority": formatPriority(ticket.priority),
      "Status": formatStatus(ticket.status),
      "Category": ticket.category || '',
      "Source": ticket.source || '',
      "Assignee": ticket.assignee || 'Unassigned',
      "Created Date": formatDate(ticket.created, { includeTime: false }),
      "Linked Order": ticket.linkedOrder || ''
    }));
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      const ticketsToExport = tickets.filter(t => selectedRows.has(t.id || t._id || t.ticketId));
      const exportData = mapTicketsForExport(ticketsToExport);

      exportToCSV(exportData, 'tickets_selected', {
        title: 'Support Tickets List',
        type: 'Selected Tickets'
      });

      setSelectedRows(new Set()); // Optional: clear selection
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    const exportData = mapTicketsForExport(tickets);
    exportToCSV(exportData, 'tickets_page', {
      title: 'Support Tickets List',
      type: 'Current Page'
    });
    setShowExportModal(false);
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const filters = {
        search: debouncedSearchTerm.trim() || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        priority: filterPriority !== 'all' ? filterPriority : undefined,
        startDate,
        endDate
      };

      const result = await ticketService.exportTickets(filters);

      if (result.success && result.downloadUrl) {
        await downloadFromBackend(result.downloadUrl, generateExportFilename('tickets_range'));
        if (window._customAlert && window._customAlert.showSuccess) {
          window._customAlert.showSuccess('Export downloaded successfully');
        }
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      if (window._customAlert && window._customAlert.showError) {
        window._customAlert.showError('Failed to export tickets');
      } else {
        alert('Failed to export tickets');
      }
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const filters = {
        search: debouncedSearchTerm.trim() || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        priority: filterPriority !== 'all' ? filterPriority : undefined
      };

      const result = await ticketService.exportTickets(filters);

      if (result.success && result.downloadUrl) {
        await downloadFromBackend(result.downloadUrl, generateExportFilename('tickets_all'));
        if (window._customAlert && window._customAlert.showSuccess) {
          window._customAlert.showSuccess('Export downloaded successfully');
        }
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      if (window._customAlert && window._customAlert.showError) {
        window._customAlert.showError('Failed to export tickets');
      } else {
        alert('Failed to export tickets');
      }
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatPriority = (priority) => {
    if (!priority) return "Medium";
    const normalized = priority.toString().toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "bg-blue-100 text-blue-800";
      case "in-progress":
        return "bg-yellow-100 text-yellow-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Capitalize first letter of status for consistent display
  const formatStatus = (status) => {
    if (!status) return "Open";
    const normalized = status.toString().toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const getSlaStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case "on-track":
        return "bg-green-100 text-green-800";
      case "at-risk":
        return "bg-yellow-100 text-yellow-800";
      case "breached":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getSlaStatusIcon = (slaStatus) => {
    switch (slaStatus) {
      case "on-track":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "at-risk":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case "breached":
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-[#0F2942]" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  // Tickets are already filtered and paginated by backend

  const getTimeRemaining = (sla) => {
    const now = new Date();
    const slaDate = new Date(sla);
    const diff = slaDate - now;

    if (diff < 0) return "Overdue";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h remaining`;
    }

    return `${hours}h ${minutes}m remaining`;
  };

  // React Query: Fetch ticket by ID
  const {
    data: ticketDetailData,
    isLoading: loadingTicketDetail
  } = useQuery({
    queryKey: ['ticketDetail', ticketIdForDetail],
    queryFn: async () => {
      if (!ticketIdForDetail) return null;
      const result = await ticketService.getTicketById(ticketIdForDetail);
      if (result.success) {
        return result.ticket;
      }
      throw new Error(result.error || 'Failed to load ticket details');
    },
    enabled: !!ticketIdForDetail
  });

  // Update editingTicket when ticketDetailData changes and edit modal is open
  useEffect(() => {
    if (ticketDetailData && showEditModal) {
      const ticketData = ticketDetailData;

      // Set Customer object for dropdown
      if (ticketData.customerId) {
        const cust = ticketData.customerId;
        setSelectedCustomer({
          companyName: cust.companyName || ticketData.businessName || '',
          contactPerson: cust.contactPerson || cust.name || ticketData.customerName || '',
          name: cust.name || ticketData.customerName || '',
          _id: cust._id || cust.id || ticketData.customerId
        });
      }

      setEditingTicket({
        _id: ticketData._id,
        id: ticketData.ticketId,
        ticketId: ticketData.ticketId,
        title: ticketData.title,
        description: ticketData.description,
        priority: ticketData.priority,
        status: ticketData.status,
        category: ticketData.category || 'general',
        subCategory: ticketData.subCategory || '',
        source: ticketData.source || 'manual',
        resolution: ticketData.resolution || '',
        linkedOrder: ticketData.orderId?._id || ticketData.orderId?.id || ticketData.orderId || '',
        assignee: ticketData.assignee?._id || ticketData.assignee || '',
        assigneeId: ticketData.assignee?._id || ticketData.assignee || '',
        business: ticketData.businessName || ''
      });

      // Set Assignee object
      if (ticketData.assignee && typeof ticketData.assignee === 'object') {
        setEditingAssignee(ticketData.assignee);
      }
    }
  }, [ticketDetailData, showEditModal]);

  // React Query: Fetch support users for assignee
  const { data: supportUsersData } = useQuery({
    queryKey: ['supportUsers'],
    queryFn: async () => {
      const result = await userService.getSupportUsers('');
      if (result.success) {
        return result.users || [];
      }
      return [];
    }
  });



  // **NEW: Edit Ticket Handler**
  const handleEditTicket = async (ticket) => {
    const id = ticket._id || ticket.id;
    setTicketIdForDetail(id);

    // Set immediate partial state from the list item so the modal doesn't look empty while loading details
    setEditingTicket({
      _id: ticket._id || ticket.id,
      id: ticket.ticketId || ticket.id,
      title: ticket.title || '',
      description: ticket.description || '',
      priority: ticket.priority || 'medium',
      status: ticket.status || 'open',
      category: ticket.category || 'general',
      subCategory: ticket.subCategory || '',
      source: ticket.source || 'manual',
      resolution: ticket.resolution || '',
      linkedOrder: ticket.linkedOrderId || '',
      assignee: ticket.assigneeId || (ticket.assignee && ticket.assignee._id) || '',
      assigneeId: ticket.assigneeId || (ticket.assignee && ticket.assignee._id) || '',
      customer: ticket.customer || '',
      business: ticket.business || ''
    });

    if (ticket.customer) {
      setSelectedCustomer({
        companyName: ticket.business || '',
        contactPerson: ticket.customer || '',
        name: ticket.customer || '',
        id: ticket.customerId || null,
        _id: ticket.customerId || null
      });
    }

    setShowEditModal(true);
  };

  // Handle view ticket (detail page)
  const handleViewTicket = (ticket) => {
    const id = ticket._id || ticket.id;
    navigate(`/crm/tickets/${id}`);
  };



  // React Query: Delete ticket mutation
  const deleteTicketMutation = useMutation({
    mutationFn: async (ticketId) => {
      const result = await ticketService.deleteTicket(ticketId);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || 'Failed to delete ticket');
    },
    onSuccess: () => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticketKPIs'] });

      if (window._customAlert && window._customAlert.showSuccess) {
        window._customAlert.showSuccess('Ticket deleted successfully!');
      } else {
        alert('Ticket deleted successfully!');
      }
    },
    onError: (error) => {
      if (window._customAlert && window._customAlert.showError) {
        window._customAlert.showError(error.message || 'Failed to delete ticket. Please try again.');
      } else {
        alert(error.message || 'Failed to delete ticket. Please try again.');
      }
    }
  });

  // React Query: Bulk delete tickets mutation
  const bulkDeleteTicketsMutation = useMutation({
    mutationFn: async (ticketIds) => {
      const result = await ticketService.bulkDeleteTickets(ticketIds);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || 'Failed to delete tickets');
    },
    onSuccess: () => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticketKPIs'] });
      setSelectedRows(new Set());

      if (window._customAlert && window._customAlert.showSuccess) {
        window._customAlert.showSuccess('Tickets deleted successfully!');
      } else {
        alert('Tickets deleted successfully!');
      }
    },
    onError: (error) => {
      if (window._customAlert && window._customAlert.showError) {
        window._customAlert.showError(error.message || 'Failed to delete tickets. Please try again.');
      } else {
        alert(error.message || 'Failed to delete tickets. Please try again.');
      }
    }
  });

  // **NEW: Bulk Delete Handler**
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    if (isDeleting || bulkDeleteTicketsMutation.isPending) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = () => {
    setIsDeleting(true);
    bulkDeleteTicketsMutation.mutate(Array.from(selectedRows), {
      onSettled: () => {
        setIsDeleting(false);
        setShowBulkDeleteConfirm(false);
      }
    });
  };

  // **NEW: Delete Ticket Handler**
  const handleDeleteTicket = async (id, e, ticketId) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isDeleting || deleteTicketMutation.isPending) return;

    try {
      const confirmMessage = `Are you sure you want to delete ticket ${ticketId || id}? This action cannot be undone.`;
      const confirmResult = window.confirm(confirmMessage);

      let confirmed = false;
      if (confirmResult instanceof Promise) {
        confirmed = await confirmResult;
      } else {
        confirmed = confirmResult === true;
      }

      if (confirmed === true) {
        setIsDeleting(true);
        deleteTicketMutation.mutate(id);
      }
    } catch (error) {
    } finally {
      setIsDeleting(false);
    }
  };

  // React Query: Update ticket mutation
  const updateTicketMutation = useMutation({
    mutationFn: async ({ ticketId, updateData }) => {
      const result = await ticketService.updateTicket(ticketId, updateData);
      if (result.success) {
        return { result, ticketId };
      }
      throw new Error(result.error || 'Failed to update ticket');
    },
    onSuccess: ({ ticketId }) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticketDetail', ticketId] });

      setShowEditModal(false);
      setEditingTicket(null);
      setEditingAssignee(null);
      setTicketIdForDetail(null);



      alert('Ticket updated successfully!');
    },
    onError: (error) => {
      alert(error.message || 'Failed to update ticket');
    }
  });

  // **NEW: Update Ticket Handler**
  const handleUpdateTicket = async (updatedTicket) => {
    const requiredFields = ['title', 'description'];
    const validation = validateRequiredFields(updatedTicket, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    const newErrors = {};
    if (updatedTicket.title && !VALID_TEXT_REGEX.test(updatedTicket.title)) {
      newErrors.title = 'Title contains invalid characters';
    }

    if (updatedTicket.description && !VALID_TEXT_REGEX.test(updatedTicket.description)) {
      newErrors.description = 'Description contains invalid characters';
    }

    if (updatedTicket.resolution && !VALID_TEXT_REGEX.test(updatedTicket.resolution)) {
      newErrors.resolution = 'Resolution note contains invalid characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    // Use database ID (_id) for the API call if available
    const ticketId = updatedTicket._id || updatedTicket.id || updatedTicket.ticketId;

    // Safely derive orderId - treat "None" / "No orders" as no selection
    const linkedOrderValue = (updatedTicket.linkedOrder || '').toString().trim();
    const orderId =
      linkedOrderValue &&
        linkedOrderValue !== 'none' &&
        linkedOrderValue !== 'no_orders'
        ? linkedOrderValue
        : null;

    const updateData = {
      title: updatedTicket.title.trim(),
      description: updatedTicket.description.trim(),
      priority: updatedTicket.priority,
      status: updatedTicket.status,
      // If customer was changed via SearchableDropdown, selectedCustomer will have new ID
      customerId: selectedCustomer?._id || selectedCustomer?.id || updatedTicket.customerId || null,
      customerName: updatedTicket.customer || (selectedCustomer?.name || ''),
      businessName: updatedTicket.business || (selectedCustomer?.companyName || ''),
      assignee: updatedTicket.assigneeId || updatedTicket.assignee || null,
      category: updatedTicket.category || 'general',
      subCategory: updatedTicket.subCategory || '',
      source: updatedTicket.source || 'manual',
      resolution: updatedTicket.resolution || '',
      orderId: orderId
    };

    updateTicketMutation.mutate({
      ticketId,
      updateData
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
              <Ticket className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Tickets</h1>
              <p className="text-sm sm:text-base text-slate-500 mt-1">Manage customer support tickets</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {selectedRows.size > 0 && (
              <PermissionGate permission="customer_management" action="delete">
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting || bulkDeleteTicketsMutation.isPending}
                  className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete ({selectedRows.size})</span>
                </button>
              </PermissionGate>
            )}
            <PermissionGate permission="customer_management" action="read">
              <button
                onClick={handleExportClick}
                className="bg-white border border-slate-900 hover:bg-slate-50 text-slate-900 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!tickets || tickets.length === 0}
              >
                <Download className="h-4 w-4" />
                <span>{selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}</span>
              </button>
            </PermissionGate>
            <PermissionGate permission="customer_management" action="create">
              <button
                onClick={() => setShowBulkUpload(true)}
                className="bg-white border border-slate-900 hover:bg-slate-50 text-slate-900 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              >
                <Upload className="h-4 w-4" />
                <span>Bulk Upload</span>
              </button>
            </PermissionGate>
            <PermissionGate permission="customer_management" action="create">
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              >
                <Plus className="h-4 w-4" />
                <span>Create Ticket</span>
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {/* 1. Total Tickets */}
        <div
          onClick={() => handleFilterChange('all')}
          className="bg-white p-4 rounded-2xl border border-blue-200/80 shadow-2xs hover:shadow-md hover:border-blue-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-blue-600 transition-colors">Total Tickets</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-slate-900 mt-1 group-hover:text-blue-900 transition-colors">
                  {kpis.totalTickets}
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center border border-blue-100 group-hover:scale-110 transition-transform">
              <FileText className="h-5 w-5" />
            </div>
          </div>
        </div>

        {/* 2. Open */}
        <div
          onClick={() => handleFilterChange('open')}
          className="bg-white p-4 rounded-2xl border border-cyan-200/80 shadow-2xs hover:shadow-md hover:border-cyan-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-cyan-600 transition-colors">Open</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-slate-900 mt-1 group-hover:text-cyan-900 transition-colors">
                  {kpis.openTickets}
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-cyan-50 text-cyan-600 rounded-full flex items-center justify-center border border-cyan-100 group-hover:scale-110 transition-transform">
              <Clock className="h-5 w-5" />
            </div>
          </div>
        </div>

        {/* 3. In Progress */}
        <div
          onClick={() => handleFilterChange('in-progress')}
          className="bg-white p-4 rounded-2xl border border-amber-200/80 shadow-2xs hover:shadow-md hover:border-amber-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-amber-600 transition-colors">In Progress</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-slate-900 mt-1 group-hover:text-amber-900 transition-colors">
                  {kpis.inProgressTickets}
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center border border-amber-100 group-hover:scale-110 transition-transform">
              <Clock className="h-5 w-5" />
            </div>
          </div>
        </div>

        {/* 4. Pending */}
        <div
          onClick={() => handleFilterChange('pending')}
          className="bg-white p-4 rounded-2xl border border-orange-200/80 shadow-2xs hover:shadow-md hover:border-orange-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-orange-600 transition-colors">Pending</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-slate-900 mt-1 group-hover:text-orange-900 transition-colors">
                  {kpis.pendingTickets}
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-orange-50 text-orange-600 rounded-full flex items-center justify-center border border-orange-100 group-hover:scale-110 transition-transform">
              <AlertTriangle className="h-5 w-5" />
            </div>
          </div>
        </div>

        {/* 5. Resolved */}
        <div
          onClick={() => handleFilterChange('resolved')}
          className="bg-white p-4 rounded-2xl border border-emerald-200/80 shadow-2xs hover:shadow-md hover:border-emerald-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-emerald-600 transition-colors">Resolved</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-slate-900 mt-1 group-hover:text-emerald-900 transition-colors">
                  {kpis.resolvedTickets}
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center border border-emerald-100 group-hover:scale-110 transition-transform">
              <CheckCircle className="h-5 w-5" />
            </div>
          </div>
        </div>

        {/* 6. Closed */}
        <div
          onClick={() => handleFilterChange('closed')}
          className="bg-white p-4 rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md hover:border-slate-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-slate-700 transition-colors">Closed</p>
              {loadingKPIs ? (
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-slate-900 mt-1 group-hover:text-slate-900 transition-colors">
                  {kpis.closedTickets}
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-slate-100 text-slate-600 rounded-full flex items-center justify-center border border-slate-200 group-hover:scale-110 transition-transform">
              <XCircle className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Tickets List */}
      <div className="space-y-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-2xs border border-slate-200/90 p-4 sm:p-6">
          <div className="flex flex-nowrap gap-3 items-center overflow-x-auto p-1 -m-1">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search tickets by title, ID, customer, business, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.replace(INVALID_CHARS_REGEX, ''))}
                maxLength={FIELD_LIMITS.search}
                className="input-field pl-10 pr-4 py-2 w-full"
                autoComplete="off"
                autoFocus
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="input-field flex-shrink-0 w-auto px-3 py-2 text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={filterPriority}
              onChange={(e) => handlePriorityFilterChange(e.target.value)}
              className="input-field flex-shrink-0 w-auto px-3 py-2 text-sm"
            >
              <option value="all">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(newRange) => {
                setDateRange(newRange);
                setCurrentPage(1);
              }}
              allowFutureDates={false}
            />
            {(searchTerm || filterStatus !== 'all' || filterPriority !== 'all' || dateRange.startDate || dateRange.endDate) && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setDebouncedSearchTerm('');
                  setFilterStatus('all');
                  setFilterPriority('all');
                  setDateRange({ startDate: '', endDate: '' });
                  setCurrentPage(1);
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
                className="btn-cancel flex items-center justify-center gap-2 flex-shrink-0 whitespace-nowrap px-4 py-2 h-[42px]"
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </button>
            )}
          </div>
        </div>

        {/* Tickets List */}
        <div className="bg-white rounded-2xl shadow-2xs border border-slate-200/90 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-[#0F2942] border-gray-300 rounded focus:ring-[#0F2942]/50"
                      checked={tickets.length > 0 && selectedRows.size === tickets.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('ticketId')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Ticket ID</span>
                      {sortColumn === 'ticketId' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Title</span>
                      {sortColumn === 'title' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('customer')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Customer</span>
                      {sortColumn === 'customer' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('priority')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Priority</span>
                      {sortColumn === 'priority' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('created')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created Date</span>
                      {sortColumn === 'created' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('assignee')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Assignee</span>
                      {sortColumn === 'assignee' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingTickets ? (
                  // Loading skeletons
                  Array.from({ length: itemsPerPage }).map((_, idx) => (
                    <tr key={`skeleton-${idx}`} className="animate-pulse">
                      <td className="px-6 py-4"><div className="h-4 w-4 bg-gray-200 rounded"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-40"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                      <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded w-16"></div></td>
                      <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded w-16"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    </tr>
                  ))
                ) : tickets.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      No tickets found matching your criteria.
                    </td>
                  </tr>
                ) : (
                  getSortedTickets(tickets).map((ticket) => {
                    const customerFull = ticket.customer || '';
                    const businessFull = ticket.business || '';
                    const assigneeFull = ticket.assignee || '';
                    const ticketIdFull = ticket.ticketId || '';
                    const titleFull = ticket.title || '';

                    return (
                      <tr key={ticket.id || ticket._id} className="hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-[#0F2942] border-gray-300 rounded focus:ring-[#0F2942]/50"
                            checked={selectedRows.has(ticket.id || ticket._id || ticket.ticketId)}
                            onChange={() => handleSelectRow(ticket.id || ticket._id || ticket.ticketId)}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className="text-sm font-medium text-gray-900 truncate max-w-[120px]"
                            title={ticketIdFull}
                          >
                            {ticketIdFull}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className="text-sm font-medium text-gray-900 truncate max-w-[200px] capitalize"
                            title={titleFull}
                          >
                            {truncateText(titleFull, 50)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="max-w-[200px]">
                            <div
                              className="text-sm font-medium text-gray-900 truncate"
                              title={customerFull}
                            >
                              {customerFull ? customerFull.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : ''}
                            </div>
                            <div
                              className="text-sm text-gray-500 truncate"
                              title={businessFull}
                            >
                              {businessFull ? businessFull.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : ''}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(ticket.priority)}`}
                            title={ticket.priority ? `${formatPriority(ticket.priority)} Priority` : ''}
                          >
                            {formatPriority(ticket.priority)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(ticket.status)}`}
                            title={ticket.status ? `${formatStatus(ticket.status)} Status` : ''}
                          >
                            {formatStatus(ticket.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(ticket.created, { includeTime: true })}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className="text-sm text-gray-900 truncate max-w-[150px]"
                            title={assigneeFull}
                          >
                            {assigneeFull}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <PermissionGate permission="customer_management" action="read">
                              <button
                                onClick={() => handleViewTicket(ticket)}
                                className="text-slate-900 hover:text-slate-700"
                                title="View Ticket"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="customer_management" action="update">
                              <button
                                onClick={() => handleEditTicket(ticket)}
                                className="text-slate-900 hover:text-slate-700"
                                title="Edit Ticket"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="customer_management" action="delete">
                              <button
                                onClick={(e) => handleDeleteTicket(ticket._id || ticket.id, e, ticket.ticketId)}
                                disabled={isDeleting || deleteTicketMutation.isPending}
                                className="text-slate-900 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete Ticket"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-6">
          <PaginationControls
            currentPage={pagination?.currentPage || 1}
            totalPages={pagination?.totalPages || 1}
            totalItems={pagination?.totalItems || 0}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </div >

      {/* **NEW: Create Ticket Modal** */}
      {
        showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !mt-0 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Create New Ticket
                  </h3>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setErrors({});
                      setNewTicket({
                        title: "",
                        description: "",
                        customer: "",
                        business: "",
                        priority: "medium",
                        category: "general",
                        subCategory: "",
                        source: "manual",
                        assignee: "",
                        linkedOrder: "",
                        tags: [],
                        attachments: [],
                      });
                      setSelectedCustomer(null);
                      setSelectedAssignee(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto flex-1" style={{ minHeight: 0 }}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={newTicket.title}
                        onChange={(e) => {
                          const sanitized = e.target.value.replace(INVALID_CHARS_REGEX, '');
                          setNewTicket({ ...newTicket, title: sanitized });
                          if (errors.title) {
                            setErrors(prev => ({ ...prev, title: '' }));
                          }
                        }}
                        className={`input-field w-full px-3 py-2 ${errors.title ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                      />
                      <div className="flex items-start justify-between mt-1 gap-2">
                        {errors.title ? (
                          <p className="text-xs text-red-600 flex-1">{errors.title}</p>
                        ) : (
                          <div></div>
                        )}
                        <div className="text-xs text-gray-500 whitespace-nowrap">
                          {getCharacterCount(newTicket.title, 100)}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority
                      </label>
                      <select
                        value={newTicket.priority}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, priority: e.target.value })
                        }
                        className="input-field w-full px-3 py-2"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={newTicket.description}
                      onChange={(e) => {
                        const sanitized = e.target.value.replace(INVALID_CHARS_REGEX, '');
                        setNewTicket({
                          ...newTicket,
                          description: sanitized,
                        });
                        if (errors.description) {
                          setErrors(prev => ({ ...prev, description: '' }));
                        }
                      }}
                      rows={3}
                      className={`input-field w-full px-3 py-2 ${errors.description ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                    />
                    <div className="flex items-start justify-between mt-1 gap-2">
                      {errors.description ? (
                        <p className="text-xs text-red-600 flex-1 break-words">{errors.description}</p>
                      ) : (
                        <div></div>
                      )}
                      <div className="text-xs text-gray-500 whitespace-nowrap">
                        {getCharacterCount(newTicket.description, FIELD_LIMITS.notes)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <SearchableDropdown
                        label="Customer"
                        placeholder="Search customer..."
                        selected={selectedCustomer}
                        onSelect={handleCustomerSelect}
                        fetchDefault={fetchTopCustomers}
                        fetchSearch={fetchCustomerSearch}
                        renderItem={renderCustomerItem}
                        required={true}
                        error={errors.customer}
                        onBlur={() => handleCreateTicketBlur('customer')}
                      />
                      {/* {errors.customer && (
                        <p className="text-xs text-red-600 mt-1">{errors.customer}</p>
                      )} */}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Name
                      </label>
                      <input
                        type="text"
                        value={newTicket.business}
                        readOnly
                        className="input-field w-full px-3 py-2 bg-gray-50"
                        placeholder="Auto-filled from customer"
                      />
                    </div>
                  </div>

                  {/* Linked Order, Category, Source, Assignee - 2 fields per row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Linked Order
                      </label>
                      <select
                        value={newTicket.linkedOrder}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, linkedOrder: e.target.value })
                        }
                        disabled={!selectedCustomer || loadingOrders}
                        className="input-field w-full px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">None</option>
                        {loadingOrders ? (
                          <option value="" disabled>Loading orders...</option>
                        ) : customerOrders.length === 0 ? (
                          <option value="" disabled>No orders found</option>
                        ) : (
                          customerOrders.map((order) => (
                            <option key={order._id} value={order._id}>
                              {order.orderNumber || order.orderId} ({order.status})
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={newTicket.category}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, category: e.target.value })
                        }
                        className="input-field w-full px-3 py-2"
                      >
                        {ticketCategories.map((category) => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Source
                      </label>
                      <select
                        value={newTicket.source}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, source: e.target.value })
                        }
                        className="input-field w-full px-3 py-2"
                      >
                        {ticketSources.map((source) => (
                          <option key={source.value} value={source.value}>
                            {source.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="relative">
                      <SearchableDropdown
                        label="Assignee"
                        placeholder="Search assignee..."
                        selected={selectedAssignee}
                        onSelect={handleAssigneeSelect}
                        fetchDefault={fetchTopSupportUsers}
                        fetchSearch={fetchSupportUserSearch}
                        renderItem={renderAssigneeItem}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setErrors({});
                      setNewTicket({
                        title: "",
                        description: "",
                        customer: "",
                        business: "",
                        priority: "medium",
                        category: "general",
                        subCategory: "",
                        source: "manual",
                        assignee: "",
                        linkedOrder: "",
                        tags: [],
                        attachments: [],
                      });
                      setSelectedCustomer(null);
                      setSelectedAssignee(null);
                    }}
                    className="btn-cancel px-6 h-10"
                  >
                    <span>Cancel</span>
                  </button>
                  <PermissionGate permission="customer_management" action="create">
                    <button
                      onClick={handleCreateTicket}
                      disabled={createTicketMutation.isPending}
                      className="btn-create px-6 h-10 flex items-center justify-center"
                    >
                      <span>{createTicketMutation.isPending ? 'Creating...' : 'Create Ticket'}</span>
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }



      {/* **NEW: Edit Ticket Modal** */}
      {
        showEditModal && editingTicket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Edit Ticket - {editingTicket.id}
                  </h3>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTicket(null);
                      setEditingAssignee(null);
                      setSelectedCustomer(null);
                      setTicketIdForDetail(null);
                      setErrors({});
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="px-6 py-4">
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={editingTicket.title}
                        onChange={(e) => {
                          const sanitized = e.target.value.replace(INVALID_CHARS_REGEX, '');
                          setEditingTicket(prev => ({ ...prev, title: sanitized }));
                          if (errors.title) {
                            setErrors(prev => ({ ...prev, title: '' }));
                          }
                        }}
                        className={`input-field w-full px-3 py-2 pr-16 ${errors.title ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                        {getCharacterCount(editingTicket.title, FIELD_LIMITS.title)}
                      </div>
                    </div>
                    {errors.title && (
                      <p className="text-xs text-red-600 mt-1">{errors.title}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <textarea
                        value={editingTicket.description}
                        onChange={(e) => {
                          const sanitized = e.target.value.replace(INVALID_CHARS_REGEX, '');
                          setEditingTicket(prev => ({ ...prev, description: sanitized }));
                          if (errors.description) {
                            setErrors(prev => ({ ...prev, description: '' }));
                          }
                        }}
                        rows={4}
                        className={`input-field w-full px-3 py-2 pr-16 ${errors.description ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                      />
                      <div className="absolute right-2 bottom-2 text-xs text-gray-500">
                        {getCharacterCount(editingTicket.description, FIELD_LIMITS.notes)}
                      </div>
                    </div>
                    {errors.description && (
                      <p className="text-xs text-red-600 mt-1">{errors.description}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority
                      </label>
                      <select
                        value={editingTicket.priority}
                        onChange={(e) => setEditingTicket(prev => ({ ...prev, priority: e.target.value }))}
                        className="input-field w-full px-3 py-2"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={editingTicket.status}
                        onChange={(e) => setEditingTicket(prev => ({ ...prev, status: e.target.value }))}
                        className="input-field w-full px-3 py-2"
                      >
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="pending">Pending</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Customer (Read Only in Edit mostly, or Editable? Let's make it match create but maybe read-only if logic requires) */}
                    {/* User requested to use Create form, so we make it editable */}
                    <div>
                      <SearchableDropdown
                        label="Customer"
                        placeholder="Search customer..."
                        selected={selectedCustomer}
                        onSelect={handleCustomerSelect}
                        fetchDefault={fetchTopCustomers}
                        fetchSearch={fetchCustomerSearch}
                        renderItem={renderCustomerItem}
                        required={true}
                        error={errors.customer}
                      // Note: Customer might be locked in edit in some systems, but user asked for Create form consistency
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Name
                      </label>
                      <input
                        type="text"
                        value={editingTicket.business || ''}
                        readOnly
                        className="input-field w-full px-3 py-2 bg-gray-50"
                        placeholder="Auto-filled"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={editingTicket.category}
                        onChange={(e) =>
                          setEditingTicket(prev => ({ ...prev, category: e.target.value }))
                        }
                        className="input-field w-full px-3 py-2"
                      >
                        {ticketCategories.map((category) => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Source */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Source
                      </label>
                      <select
                        value={editingTicket.source}
                        onChange={(e) =>
                          setEditingTicket(prev => ({ ...prev, source: e.target.value }))
                        }
                        className="input-field w-full px-3 py-2"
                      >
                        {ticketSources.map((source) => (
                          <option key={source.value} value={source.value}>
                            {source.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Linked Order */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Linked Order
                      </label>
                      <select
                        value={editingTicket.linkedOrder || ''}
                        onChange={(e) => setEditingTicket(prev => ({ ...prev, linkedOrder: e.target.value }))}
                        disabled={loadingOrders}
                        className="input-field w-full px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">None</option>
                        {loadingOrders ? (
                          <option value="" disabled>Loading orders...</option>
                        ) : customerOrders.length === 0 ? (
                          <option value="" disabled>No orders found</option>
                        ) : (
                          customerOrders.map((order) => (
                            <option key={order._id} value={order._id}>
                              {order.orderNumber || order.orderId} ({order.status})
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    {/* Assignee */}
                    <div>
                      <SearchableDropdown
                        label="Assignee"
                        placeholder="Search assignee..."
                        selected={editingAssignee}
                        onSelect={(assignee) => {
                          setEditingAssignee(assignee);
                          setEditingTicket(prev => ({
                            ...prev,
                            assignee: assignee ? (assignee._id || assignee.id) : '',
                            assigneeId: assignee ? (assignee._id || assignee.id) : ''
                          }));
                        }}
                        fetchDefault={fetchTopSupportUsers}
                        fetchSearch={fetchSupportUserSearch}
                        renderItem={renderAssigneeItem}
                      />
                    </div>
                  </div>

                  {/* Resolution (Specific to Edit) */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Resolution Note
                    </label>
                    <textarea
                      value={editingTicket.resolution || ''}
                      onChange={(e) => {
                        const sanitized = e.target.value.replace(INVALID_CHARS_REGEX, '');
                        setEditingTicket(prev => ({ ...prev, resolution: sanitized }));
                        if (errors.resolution) {
                          setErrors(prev => ({ ...prev, resolution: '' }));
                        }
                      }}
                      rows={3}
                      className={`input-field w-full px-3 py-2 ${errors.resolution ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                      placeholder="Enter resolution details if resolving/closing..."
                      maxLength={FIELD_LIMITS.notes}
                    />
                    {errors.resolution && (
                      <p className="text-xs text-red-600 mt-1">{errors.resolution}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTicket(null);
                      setEditingAssignee(null);
                      setSelectedCustomer(null);
                      setTicketIdForDetail(null);
                      setErrors({});
                    }}
                    className="btn-cancel px-6 h-10"
                  >
                    <span>Cancel</span>
                  </button>
                  <PermissionGate permission="customer_management" action="update">
                    <button
                      onClick={() => handleUpdateTicket(editingTicket)}
                      disabled={updateTicketMutation.isPending}
                      className="btn-create px-6 h-10 flex items-center justify-center"
                    >
                      <span>{updateTicketMutation.isPending ? 'Updating...' : 'Update Ticket'}</span>
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportRange={handleExportRange}
        onExportAll={handleExportAll}
        loading={exportLoading}
        title="Export Tickets"
        allDataLabel="Export All Filtered Tickets"
        showDateRange={true}
      />

      <ConfirmationDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selectedRows.size} selected ticket(s)? This action cannot be undone.`}
        confirmText="Delete All"
        type="danger"
      />
      {/* Bulk Upload Modal */}
      <BulkTicketUpload
        isOpen={showBulkUpload}
        onCancel={() => setShowBulkUpload(false)}
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['tickets'] });
          queryClient.invalidateQueries({ queryKey: ['ticketKPIs'] });
        }}
      />
    </div>
  );
};

export default TicketsFeedback;
